import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Wallet, Link2, ArrowLeftRight, Coins } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const steps = [
  {
    step: '01',
    icon: Wallet,
    title: 'Connect Your Wallet',
    description: 'Link your Polk Wallet or any compatible Web3 wallet to access the ecosystem.',
  },
  {
    step: '02',
    icon: Link2,
    title: 'Interact with Polk Chain',
    description: 'Execute transactions, deploy smart contracts, or interact with DApps.',
  },
  {
    step: '03',
    icon: ArrowLeftRight,
    title: 'Use Polk Swap & DApps',
    description: 'Trade tokens, provide liquidity, or explore our growing DApp ecosystem.',
  },
  {
    step: '04',
    icon: Coins,
    title: 'Earn & Transact',
    description: 'Earn rewards in Polk Coin and use PUSD for stable value transactions.',
  },
];

export const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="how-it-works"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#F1F5F9]'}`}
      ref={ref}
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            How It Works
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Get Started in <span className="gradient-text">4 Simple Steps</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Join the Polk Chain ecosystem and start your decentralized journey today.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent -translate-y-1/2" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 * index }}
                className="relative"
              >
                <div className={`h-full p-8 rounded-3xl backdrop-blur-xl text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                  isDark 
                    ? 'bg-white/[0.03] border border-white/10' 
                    : 'bg-white border border-black/5'
                }`}>
                  {/* Step number */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-blue-600 text-white text-sm font-bold">
                    {item.step}
                  </div>

                  {/* Icon */}
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-6 mt-4">
                    <item.icon className="w-10 h-10 text-blue-400" />
                  </div>

                  {/* Content */}
                  <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {item.title}
                  </h3>
                  <p className={isDark ? 'text-zinc-400' : 'text-slate-600'}>
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
